// import { isEqual } from "lodash";
// import { Stopwatch } from "performance-stopwatch";

import { TimelineRecord } from "../../types/immutableRecords/TimelineRecord";
import rootActionTypes from "../rootActionTypes";
import displayItems from "./displayItems";
import itemDates from "./itemDates";
import lastTodayDate from "./lastTodayDate";
import validatedDateConstraints from "./validatedDateConstraints";
import validatedDependencies from "./validatedDependencies";
import viewCardSizes from "./viewCardSizes";

const reducerSteps = [
  { key: "lastTodayDate", func: lastTodayDate },
  { key: "itemDates", func: itemDates },
  { key: "validatedDateConstraints", func: validatedDateConstraints },
  { key: "validatedDependencies", func: validatedDependencies },
  { key: "displayItems", func: displayItems },
  { key: "viewCardSizes", func: viewCardSizes }
];

// * deriveDisplayData is responsible for calculating data that is derived from our redux state
//   where for performance reasons, we want to have access to the action that was applied
// * Theory:
//     - reselect, re-reselect etc. applies a very broad brush once data is being derived from
//       other items...
//     - for example, because we are calculating how to display related items, we need access to
//       itemsById... which means as soon as one item changes color, everything needs to be looked at.
//     - in the past we have tried to work around this with judicious use of other selectors, and of
//       JSON.stringify() to sneak past cases where we cannot help but return a new object reference
//       otherwise, but these calculations come at a cost
//     - it would be much easier if we could *know* which items are going to be affected by an operation
//     - in some cases, this will be the item IDs that appear directly in the action
//     - but it would also need to consider related items, super items, etc. where appropriate.
//
// * deriveDisplayData will pass the following to each of its internal reducers:
//     - inputState <-- the last "display" state
//     - action <-- the action that was performed (may be a batch)
//     - newGlobalState <-- the new global state
//     - oldGlobalState <-- the old global state
const deriveDisplayData = reducer => {
  // function that does the work
  return function (origState, action) {
    window.blockDisplayItemsAccess = true;

    // const sw = new Stopwatch();
    // sw.start(`deriveDisplayData ${action.type}`);

    // First, we need to calculate our new state
    let newState = reducer(origState, action);

    // sw.lap(`deriveDisplayData - reducers`);

    const isFirstLoad =
      action.type === rootActionTypes.LOAD_FILE ||
      action.type === rootActionTypes.UPDATE_FILE ||
      action.type === rootActionTypes.LOAD_SAMPLE;

    // When LOAD_FILE, UPDATE_FILE or LOAD_SAMPLE are called, the root reducer has already updated the state before we are
    // even aware of the old data. So in that special case, we need to pass in a blank previous state to make
    // sure we calculate everything we should!
    let prevState = isFirstLoad ? TimelineRecord() : origState;

    // Iterate through all of our reducer steps in order
    // Each subsequent step will receive the state updated with the last value
    // so that it can further derive values from that
    reducerSteps.forEach(obj => {
      const { key, func } = obj;
      newState = newState.updateIn(["display", key], inputState => func(inputState, action, newState, prevState));
    });

    // sw.lap(`deriveDisplayData - display data`);

    // // Debugging diagnosis
    // if (!isFirstLoad && newState.display !== prevState.display) {
    //   console.log(`deriveDisplayData(): has produced a different result`);

    //   if (prevState.display.lastTodayDate && newState.display.lastTodayDate !== prevState.display.lastTodayDate) {
    //     console.log(`\t\t newState.display.lastTodayDate object differs from prevState.display.lastTodayDate`);

    //     const isEquivalent = isEqual(newState.display.lastTodayDate.toJS(), prevState.display.lastTodayDate.toJS());
    //     if (isEquivalent) {
    //       console.log(`\t\t\t\t WARNING: lastTodayDate has same value but different reference.`);
    //     }
    //   }

    //   if (newState.display.itemDates !== prevState.display.itemDates) {
    //     console.log(`\t\t newState.display.itemDates object differs from prevState.display.itemDates`);

    //     newState.display.itemDates.forEach((newItem, id) => {
    //       const prevItem = prevState.display.itemDates.get(id);
    //       if (prevItem && newItem !== prevItem) {
    //         const isEquivalent = isEqual(newItem.toJS(), prevItem.toJS());
    //         if (isEquivalent) {
    //           console.log(`\t\t\t\t WARNING: item dates ${newItem.id} has same value but different reference.`);
    //         }
    //       }
    //     });
    //   }

    //   if (newState.display.validatedDependencies !== prevState.display.validatedDependencies) {
    //     console.log(
    //       `\t\t newState.display.validatedDependencies object differs from prevState.display.validatedDependencies`
    //     );

    //     newState.display.validatedDependencies.forEach((newItem, id) => {
    //       const prevItem = prevState.display.validatedDependencies.get(id);
    //       if (prevItem && newItem !== prevItem) {
    //         const isEquivalent = isEqual(newItem.toJS(), prevItem.toJS());
    //         if (isEquivalent) {
    //           console.log(
    //             `\t\t\t\t WARNING: validatedDependency ${newItem.id} has same value but different reference.`
    //           );
    //         }
    //       }
    //     });
    //   }

    //   if (newState.display.validatedDateConstraints !== prevState.display.validatedDateConstraints) {
    //     console.log(
    //       `\t\t newState.display.validatedDateConstraints object differs from prevState.display.validatedDateConstraints`
    //     );

    //     newState.display.validatedDateConstraints.forEach((newItem, id) => {
    //       const prevItem = prevState.display.validatedDateConstraints.get(id);
    //       if (prevItem && newItem !== prevItem) {
    //         const isEquivalent = isEqual(newItem.toJS(), prevItem.toJS());
    //         if (isEquivalent) {
    //           console.log(
    //             `\t\t\t\t WARNING: validatedDateConstraint ${newItem.id} has same value but different reference.`
    //           );
    //         }
    //       }
    //     });
    //   }

    //   if (newState.display.displayItems !== prevState.display.displayItems) {
    //     console.log(`\t\t newState.display.displayItems object differs from prevState.display.displayItems`);

    //     newState.display.displayItems.forEach((newItem, id) => {
    //       const prevItem = prevState.display.displayItems.get(id);
    //       if (prevItem && newItem !== prevItem) {
    //         const isEquivalent = isEqual(newItem.toJS(), prevItem.toJS());
    //         if (isEquivalent) {
    //           console.log(
    //             `\t\t\t\t WARNING: displayItem "${newItem.label}" (${newItem.id}) has same value but different reference.`
    //           );
    //         }
    //       }
    //     });
    //   }

    //   if (newState.display.viewCardSizes !== prevState.display.viewCardSizes) {
    //     console.log(`\t\t newState.display.viewCardSizes object differs from prevState.display.viewCardSizes`);

    //     newState.display.viewCardSizes.forEach((newItem, id) => {
    //       const prevItem = prevState.display.viewCardSizes.get(id);
    //       if (prevItem && newItem !== prevItem) {
    //         const isEquivalent = isEqual(newItem.toJS(), prevItem.toJS());
    //         if (isEquivalent) {
    //           console.log(`\t\t\t\t WARNING: viewCardSizes ${newItem.id} has same value but different reference.`);
    //         }
    //       }
    //     });
    //   }
    // }

    // And finally return that value
    return newState;
  };
};

export default deriveDisplayData;
